/**
 * Text pasted from a PDF often arrives hard-wrapped: one sentence cut into short lines. The engine
 * reads one instruction per line, so we offer to join the cut lines back (never silently).
 */

/** A line that ends a sentence, a label or a heading-like colon. */
const ENDS = /[.:;!?…)\]»"”’*]$/;
/** A line that looks like the continuation of the previous one. */
const CONTINUES = /^(?:[a-zà-öø-ÿœæ([*]|\d+(?![.)]\s))/u;

function joinable(current: string, next: string): boolean {
  const a = current.trimEnd();
  const b = next.trim();
  if (!a || !b) return false;
  if (a.startsWith('#')) return false; // front matter
  if (ENDS.test(a)) return false;
  return CONTINUES.test(b);
}

/** True when several lines look cut in the middle of a sentence. */
export function looksHardWrapped(text: string): boolean {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const nonEmpty = lines.filter((l) => l.trim() !== '').length;
  if (nonEmpty < 3) return false;
  let cuts = 0;
  for (let i = 0; i + 1 < lines.length; i++) if (joinable(lines[i], lines[i + 1])) cuts++;
  return cuts >= 2 && cuts / nonEmpty >= 0.2;
}

/** Joins every cut line with the next one. Returns the text and how many joins were made. */
export function unwrapLines(text: string): { text: string; joined: number } {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let joined = 0;
  for (const line of lines) {
    const prev = out.length ? out[out.length - 1] : null;
    if (prev !== null && joinable(prev, line)) {
      const head = prev.trimEnd();
      const glue = head.endsWith('\u00ad') ? '' : head.endsWith('-') ? '' : ' ';
      out[out.length - 1] = head.replace(/\u00ad$/, '') + glue + line.trim();
      joined++;
    } else {
      out.push(line);
    }
  }
  return { text: out.join('\n'), joined };
}
